import { useEffect, useState } from "react";
import { CheckCircle2, RotateCcw, Undo2 } from "lucide-react";
import { adminApi } from "@/api/admin";
import { ErrorState } from "@/components/ErrorState";
import { formatDate } from "@/lib/dates";
import type { Storno } from "@/types/admin";

const eur = (n: number) => "€" + n.toLocaleString("it-IT", { minimumFractionDigits: 2 });

export function Storni() {
  const [rows, setRows] = useState<Storno[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [soloDaRecuperare, setSoloDaRecuperare] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);

  function carica(soloAperti: boolean) {
    setRows(null);
    setError(null);
    adminApi
      .storni(soloAperti ? { stato: "da recuperare" } : {})
      .then(setRows)
      .catch(() => setError("Impossibile caricare gli storni."));
  }

  useEffect(() => { carica(soloDaRecuperare); }, [soloDaRecuperare]);

  async function segna(s: Storno, recuperato: boolean) {
    setBusy(s.id);
    try {
      const agg = await adminApi.segnaStorno(s.id, recuperato);
      setRows((prev) => (prev ?? []).map((r) => (r.id === s.id ? agg : r)));
    } catch {
      setError("Non sono riuscito ad aggiornare lo storno.");
    } finally {
      setBusy(null);
    }
  }

  const daRecuperare = (rows ?? []).filter((r) => r.stato_recupero === "da recuperare");
  const totale = daRecuperare.reduce((s, r) => s + r.quota_stornata, 0);

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">Storni commissione</h2>
          <p className="text-sm text-muted-foreground">
            Gettoni già riconosciuti alle agenzie su pratiche poi annullate: da recuperare
          </p>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={soloDaRecuperare}
                 onChange={(e) => setSoloDaRecuperare(e.target.checked)} />
          Solo da recuperare
        </label>
      </div>

      {daRecuperare.length > 0 && (
        <div className="bg-card rounded-lg border border-border p-4 flex items-end gap-8">
          <div>
            <div className="text-2xl font-bold text-text">{daRecuperare.length}</div>
            <div className="text-xs text-muted-foreground">da recuperare</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-text">{eur(totale)}</div>
            <div className="text-xs text-muted-foreground">importo complessivo</div>
          </div>
        </div>
      )}

      {error ? <ErrorState message={error} />
        : rows === null ? (
          <div className="space-y-2">
            {[0, 1, 2].map((i) => <div key={i} className="h-12 bg-surface rounded animate-pulse" />)}
          </div>
        ) : rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Nessuno storno {soloDaRecuperare ? "da recuperare" : "registrato"}.
          </p>
        ) : (
          <div className="bg-card rounded-lg border border-border overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-surface text-muted-foreground"><tr>
                <th className="text-left py-2 px-4 font-medium">Pratica</th>
                <th className="text-left py-2 px-4 font-medium">Agenzia</th>
                <th className="text-left py-2 px-4 font-medium">Competenza</th>
                <th className="text-right py-2 px-4 font-medium">Quota</th>
                <th className="text-right py-2 px-4 font-medium">Ricavo</th>
                <th className="text-left py-2 px-4 font-medium">Stato</th>
                <th className="py-2 px-4" />
              </tr></thead>
              <tbody>
                {rows.map((s) => {
                  const recuperato = s.stato_recupero === "recuperato";
                  return (
                    <tr key={s.id} className="border-t border-border">
                      <td className="py-2 px-4 text-text">{s.pratica_name ?? "—"}</td>
                      <td className="py-2 px-4 text-muted-foreground">{s.agenzia_name ?? "—"}</td>
                      <td className="py-2 px-4 text-text tabular-nums">{s.mese_competenza ?? "—"}</td>
                      <td className="py-2 px-4 text-right font-medium text-text tabular-nums">{eur(s.quota_stornata)}</td>
                      <td className="py-2 px-4 text-right text-muted-foreground tabular-nums">{eur(s.ricavo_stornato)}</td>
                      <td className="py-2 px-4">
                        {recuperato ? (
                          <span className="inline-flex items-center gap-1 text-xs bg-brand/10 text-brand-dark px-2 py-0.5 rounded-full">
                            <CheckCircle2 className="w-3 h-3" />
                            recuperato{s.data_recupero ? ` il ${formatDate(s.data_recupero)}` : ""}
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full">
                            <RotateCcw className="w-3 h-3" /> da recuperare
                          </span>
                        )}
                      </td>
                      <td className="py-2 px-4 text-right">
                        <button
                          onClick={() => segna(s, !recuperato)}
                          disabled={busy === s.id}
                          className="inline-flex items-center gap-1.5 text-xs border border-border rounded-lg px-2.5 py-1.5 hover:bg-surface disabled:opacity-50"
                        >
                          {recuperato ? <><Undo2 className="w-3.5 h-3.5" /> Riapri</>
                                      : <><CheckCircle2 className="w-3.5 h-3.5" /> Segna recuperato</>}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
    </div>
  );
}
